import React from 'react';
import { Trash2 } from 'lucide-react';
import { Shift } from '../types/database';
import { cn } from '../lib/utils';
import { getShiftTone } from '../lib/shift-colors';
import { TimePicker } from './ui/time-picker';
import { Field } from './ui/field';
import { Button } from './ui/button';

interface ShiftEditorProps {
  index: number;
  shift: Pick<Shift, 'clockIn' | 'clockOut' | 'hours'>;
  errors: { clockIn?: string; clockOut?: string };
  onChange: (field: 'clockIn' | 'clockOut', value: string) => void;
  /** Omit to hide the remove button (e.g. for the only shift). */
  onRemove?: () => void;
}

export const ShiftEditor: React.FC<ShiftEditorProps> = ({ index, shift, errors, onChange, onRemove }) => {
  const tone = getShiftTone(index);
  const clockInId = `shift-${index}-in`;
  const clockOutId = `shift-${index}-out`;

  return (
    <div className={cn('rounded-xl border border-l-[3px] border-line bg-raised/60 p-3.5', tone.panel)}>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[13px] font-medium text-fg">Shift {index + 1}</span>
        <div className="flex items-center gap-1.5">
          <span className="numeric rounded-md bg-white/[0.05] px-2 py-0.5 text-xs text-fg-muted">
            {shift.hours.toFixed(2)}h
          </span>
          {onRemove && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="size-7 hover:bg-danger/10 hover:text-danger"
              onClick={onRemove}
              aria-label={`Remove shift ${index + 1}`}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Clock in" htmlFor={clockInId} error={errors.clockIn}>
          <TimePicker
            id={clockInId}
            value={shift.clockIn}
            onChange={(value) => onChange('clockIn', value)}
            invalid={!!errors.clockIn}
          />
        </Field>
        <Field label="Clock out" htmlFor={clockOutId} error={errors.clockOut}>
          <TimePicker
            id={clockOutId}
            value={shift.clockOut}
            onChange={(value) => onChange('clockOut', value)}
            invalid={!!errors.clockOut}
          />
        </Field>
      </div>
    </div>
  );
};
